'use client';

import { initializeApp, getApps, getApp, type FirebaseApp } from 'firebase/app';
import {
  getMessaging,
  getToken,
  onMessage,
  isSupported,
  type Messaging,
  type MessagePayload,
} from 'firebase/messaging';
import { firebaseConfig, firebaseVapidKey, isFirebaseConfigured } from '@/lib/firebase/config';

const SERVICE_WORKER_PATH = '/firebase-messaging-sw.js';

let appInstance: FirebaseApp | null = null;
let messagingInstance: Messaging | null = null;

function getFirebaseApp(): FirebaseApp {
  if (appInstance) return appInstance;
  appInstance = getApps().length ? getApp() : initializeApp(firebaseConfig);
  return appInstance;
}

/**
 * Returns the Messaging instance, or null when push isn't available
 * (server render, unsupported browser, or missing config).
 */
export async function getMessagingIfSupported(): Promise<Messaging | null> {
  if (typeof window === 'undefined') return null;
  if (!isFirebaseConfigured()) {
    console.warn('[fcm] Firebase web config missing — set NEXT_PUBLIC_FIREBASE_* env vars.');
    return null;
  }
  if (!('serviceWorker' in navigator) || !('Notification' in window)) return null;
  if (!(await isSupported().catch(() => false))) return null;

  if (!messagingInstance) {
    messagingInstance = getMessaging(getFirebaseApp());
  }
  return messagingInstance;
}

async function registerServiceWorker(): Promise<ServiceWorkerRegistration | undefined> {
  if (!('serviceWorker' in navigator)) return undefined;
  try {
    return await navigator.serviceWorker.register(SERVICE_WORKER_PATH);
  } catch (err) {
    console.error('[fcm] service worker registration failed', err);
    return undefined;
  }
}

export type PushPermission = NotificationPermission | 'unsupported';

export async function getNotificationPermission(): Promise<PushPermission> {
  if (typeof window === 'undefined' || !('Notification' in window)) return 'unsupported';
  return Notification.permission;
}

/**
 * Requests notification permission (if not already decided) and returns the
 * FCM registration token, or null if permission was denied / unavailable.
 */
export async function requestFcmToken(): Promise<string | null> {
  const messaging = await getMessagingIfSupported();
  if (!messaging) return null;

  if (!firebaseVapidKey) {
    console.warn('[fcm] NEXT_PUBLIC_FIREBASE_VAPID_KEY is not set — cannot fetch a token.');
    return null;
  }

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return null;

  const registration = await registerServiceWorker();

  try {
    const token = await getToken(messaging, {
      vapidKey: firebaseVapidKey,
      serviceWorkerRegistration: registration,
    });
    return token || null;
  } catch (err) {
    console.error('[fcm] getToken failed', err);
    return null;
  }
}

/**
 * Subscribes to foreground messages (received while the tab is focused).
 * Returns an unsubscribe function.
 */
export async function onForegroundMessage(
  handler: (payload: MessagePayload) => void
): Promise<() => void> {
  const messaging = await getMessagingIfSupported();
  if (!messaging) return () => {};
  return onMessage(messaging, handler);
}
